import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import FormBuilderHelper from './formBuilderHelper';
import FormBuilderField from './FormBuilderField';

import './FormBuilder.scss';

export default class FormBuilder extends React.Component{
  
  constructor(props) {
    super(props);

    FormBuilder.propTypes = {
      /** The completion step being edited - either a placeholder or cloned object */
      completionStep: PropTypes.object.isRequired,
      /** Callback to mark the cs as incomplete if an edit is made to remove required data */
      handleMarkInvalid: PropTypes.func,
      /** Called to update the form data in the parent CS. */
      handleFormUpdate: PropTypes.func,
    };

    //dummy ID for latest form field not yet saved to DB
    this.dummyIdCounter = -1;

    this.state = {
      addButtonEnabled: true,
      fields: this.getInitialFormFields(),
    };
    this.addField = this.addField.bind(this);
    this.handleDeleteField = this.handleDeleteField.bind(this);
    this.handleSaveField = this.handleSaveField.bind(this);
    this.handleMarkInvalid = this.handleMarkInvalid.bind(this);

    this.handleDropping = this.handleDropping.bind(this);
    this.orderFields = this.orderFields.bind(this);
    this.sortFields = this.sortFields.bind(this);
    this.handleDragFinished = this.handleDragFinished.bind(this);
    this.handleDragging = this.handleDragging.bind(this);
    this.handlePositionDragIndicator = this.handlePositionDragIndicator.bind(this);
    this.handleCreateDragIndicator = this.handleCreateDragIndicator.bind(this);
    this.handleFindDragTarget = this.handleFindDragTarget.bind(this);
    this.handleSetDragElements = this.handleSetDragElements.bind(this);
    this.draggableField = {};

  }

  addField() {
    const { addButtonEnabled, fields } = this.state;
    if (addButtonEnabled) {
      const placeholderField = { id: this.dummyIdCounter };
      fields.push(placeholderField);
      this.dummyIdCounter--;
      this.setState({ fields, addButtonEnabled: false });
    }
  }

  getInitialFormFields() {
    const { completionStep } = this.props;
    if (completionStep.form_fields) {
      // ensure we don't modify props directly
      return completionStep.form_fields.map(field => _.cloneDeep(field));
    }
    else return [];
  }                 

  handleDeleteField(fieldId) {
    let { fields, addButtonEnabled } = this.state;
    _.remove(fields, field => field.id == fieldId);
    addButtonEnabled = this.areFieldsValid();
    this.setState({ fields, addButtonEnabled }, () => this.updateForm());
  }

  handleMarkInvalid(fieldId) {
    this.setState({ addButtonEnabled: false });
    if (this.props.handleMarkInvalid) {
      this.props.handleMarkInvalid(fieldId);
    }
  }

  /**
   * This function should only be called for a valid field. 
   * Updates the field in this.state.fields
   */
  handleSaveField(validField) {
    let { fields, addButtonEnabled } = this.state;
    const oldFieldIndex = fields.findIndex(field => field.id == validField.id);
    if (oldFieldIndex >= 0) {
      fields[oldFieldIndex] = validField;
    }
    addButtonEnabled = this.areFieldsValid();
    this.setState({ fields, addButtonEnabled }, () => this.updateForm());
  }

  areFieldsValid() {
    const { fields } = this.state;
    let isValid = true;
    for (let field of fields) {
      if (!FormBuilderHelper.isFormFieldValid(field.field_type, field.prompt, FormBuilderHelper.getOptionsStringFromFieldConfig(field))) {
        isValid = false;
        this.props.handleMarkInvalid(field.id);
      }
    }
    return isValid;
  }

  updateForm() {
    const { handleFormUpdate } = this.props;
    const updatedFields = this.state.fields.map(field => {
      const fieldCopy = { ...field };
      // remove negative indexes before sending to CS
      if (fieldCopy.id < 0) delete fieldCopy.id;
      return fieldCopy;
    });
    if (handleFormUpdate) {
      handleFormUpdate(updatedFields);
    }
  }

  renderFormFields() {
    const { fields, addButtonEnabled } = this.state;
    if (fields) {
      const hasDragDrop = (fields.length > 1 && addButtonEnabled);
    
      return fields.map(field => {
        this.draggableField[field.id] = field;
        return (
          <FormBuilderField 
            field={ field } 
            id={field.id}
            handleDeleteField={this.handleDeleteField}
            handleMarkInvalid={ this.handleMarkInvalid }
            handleSaveField={ this.handleSaveField } 
            key={ field.id }
            handleDragging={this.handleDragging}
            handleDropping={this.handleDropping}
            handleDragCancel={this.handleDragCancel}
            hasDragDrop={ hasDragDrop }
          />
        );
      });
    }
  }

  render() {
    const { fields, addButtonEnabled } = this.state;
    const isFormEmpty = !fields.length;

    return (
      <div className={'form-builder' + (isFormEmpty ? ' form-builder--empty' : '')}
      ref={element => {
        this.handleSetDragElements(element, this.outerContainer);
      }}>
        {isFormEmpty
          ? <p className='form-builder__intro-text'>{'You have created a Form!\nAdd at least one field to this form in order to save it.'}</p>
          : <div className='form-builder__fields-list' ref={element => {
            this.handleSetDragElements(this.innerContainer, element);
          }}>{this.renderFormFields()}
             
          </div>
        }

        <button 
          className={'form-builder__add-button' + (addButtonEnabled ? '' : ' form-builder__add-button--disabled')}
          onClick={this.addField} >
          ADD FIELD
        </button>
      </div>
    );
  }

  // DRAG/DROP FUNCTIONS
  handleSetDragElements(innerContainer, outerContainer) {
    if (innerContainer) {
      this.innerContainer = innerContainer;
    }
    if (outerContainer) {
      this.outerContainer = outerContainer;
    }
    if (outerContainer) {
      let parent = outerContainer.parentElement;
      while (parent) {
        if (parent.className.indexOf('form-builder') >= 0) {
          this.outerContainer = parent;
          break;
        }
        parent = parent.parentElement;
      }
    }
  }

  handleFindDragTarget(y) {
    const { fields } = this.state;
    const containerRect = this.innerContainer.getBoundingClientRect();
    const normalizeY = y - containerRect.top + 40;
    let firstInList = true;
    for (let i = 0; i < fields.length; i++) {
      if (fields[i]) {
        let id = fields[i].id;
        if (id) {
          if (this.draggableField[id]) {
            const element = document.getElementById(id);
            if (element) {
              if (
                firstInList &&
                normalizeY < element.offsetTop &&
                normalizeY <= element.offsetTop + element.offsetHeight / 2
              ) {
                this.dragBefore = true;
                return {
                  item: this.draggableField[id],
                  y: element.offsetTop,
                  x: element.offsetLeft,
                  height: element.offsetHeight,
                  width: element.offsetWidth,
                };
              } else if (
                normalizeY >= element.offsetTop &&
                normalizeY <= element.offsetTop + element.offsetHeight
              ) {
                this.dragBefore = false;
                return {
                  item: this.draggableField[id],
                  y: element.offsetTop,
                  x: element.offsetLeft,
                  height: element.offsetHeight,
                  width: element.offsetWidth,
                };
              }
              firstInList = false;
            }
          }
        }
      }
    }
  }

  handleCreateDragIndicator() {
    this.lineElement = document.createElement('div');
    this.lineElement.style.width = 'calc(100% - 200px)';
    this.lineElement.style.marginLeft = '10px';
    this.lineElement.style.height = '6px';
    this.lineElement.style.backgroundColor = '#a7a7a7';
    this.lineElement.style.position = 'absolute';
    this.lineElement.style.zIndex = 99999;
    this.innerContainer.appendChild(this.lineElement);
  }

  handlePositionDragIndicator() {
    if (this.dragTarget) {
      let top =
        this.dragTarget.y +
        this.dragTarget.height +
        6; 

      if (this.dragBefore) {
        top = this.dragTarget.y + 4;
      }

      this.lineElement.style.top = top + 'px';
      this.lineElement.style.left = this.dragTarget.x + 'px';
    }
  }

  handleDragging(field, x, y) {
    // Start in one column, and stay in that column
    if (!this.dragX) {
      const rect = this.outerContainer.getBoundingClientRect();
      this.dragObject = field;
      this.dragX = rect.left;
      this.dragTarget = this.handleFindDragTarget(y);
      this.handleCreateDragIndicator();
      if (this.dragTarget) {
        this.handlePositionDragIndicator();
      }
    } else {
      const newDragTarget = this.handleFindDragTarget(y);
      if (
        (newDragTarget && newDragTarget != this.dragTarget) ||
        this.dragBefore
      ) {
        this.dragTarget = newDragTarget;
        this.handlePositionDragIndicator();
      }
    }
    if (this.dragTarget) {
      const outerRect = this.outerContainer.getBoundingClientRect();
      if (y + 30 + this.dragTarget.height > outerRect.bottom) {
        this.outerContainer.scrollTop +=
          y + 30 + this.dragTarget.height - outerRect.bottom;
        this.handlePositionDragIndicator();
      } else if (y < outerRect.top) {
        this.outerContainer.scrollTop -= Math.abs(y - outerRect.top);
        this.handlePositionDragIndicator();
      }
    }
  }

  handleDragFinished() {
    this.innerContainer.removeChild(this.lineElement);
    delete this.dragX;
    delete this.lineElement;
    delete this.dragTarget;
    this.setState({ });
  }

  sortFields(fields) {
    fields.sort((t1, t2) => {
      if (t1.order && !t2.order) {
        return 1;
      } else if (!t1.order && t2.order) {
        return -1;
      } else if (t1.order && t2.order) {
        if (t1.order < t2.order) {
          return -1;
        } else if (t1.order > t2.order) {
          return 1;
        } else {
          return 0;
        }
      } else if (t1.id < t2.id) {
        return -1;
      } else if (t1.id > t2.id) {
        return 1;
      } else return 0;
    });
  }
 
  orderFields(fields, afterField, insertedField) {
    const orderedFields = [];
    let order = 1;
    if (!afterField) {
      insertedField.order = order;
      order++;
      orderedFields.push(insertedField);
      for (let i = 0; i < fields.length; i++) {
        const copyField = fields[i];
        if (copyField.id != insertedField.id) {
          copyField.order = order;
          orderedFields.push(copyField);
          order++;
        }
      }
    } else {
      for (let i = 0; i < fields.length; i++) {
        const copyField = fields[i];
        if (copyField.id != insertedField.id) {
          copyField.order = order++;
          orderedFields.push(copyField);
          if (copyField.id == afterField.id) {
            insertedField.order = order++;
            orderedFields.push(insertedField);
          }
        }
      }
    }
    this.setState({ fields: orderedFields }, () => {
      if (this.areFieldsValid()) {
        this.updateForm();
      }
    });
    
    return orderedFields;
  }

  handleDropping(field) {
    const { fields } = this.state;
    if (this.dragTarget) {
      let afterField = this.dragBefore ? null : this.dragTarget.item;
      if ((afterField && afterField.id != field.id) || !afterField) {
        if (fields) {
          this.sortFields([...fields]);
          this.orderFields(
            [...fields],
            afterField,
            field
          );
        }
        if (this.outerContainer) {
          this.outerContainer.scrollTop = 0;
        }
      }
    }
    this.handleDragFinished();
  }

  handleDragCancel() {
    this.handleDragFinished();
  }
}



// WEBPACK FOOTER //
// ./src/components/Forms/FormBuilder/index.js